import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { Navbar } from './components/Navbar';
import { ModernDashboard } from './components/dashboard';
import { ProfilePage } from './components/profile/ProfilePage';
import { WorkspaceLanding, ProposalWorkspace } from './components/workspace';
import {
  CostingStudio,
  PricingWizard,
  CLINBuilder,
  PriceToWinAnalysis,
  ProposalPricingView,
  LaborCategoriesPage,
  IndirectRatesPage,
  ODCCategoriesPage,
  WrapRateCalculator
} from './components/workspace/costing';
import { SubscriptionPage } from './components/subscription/SubscriptionPage';
import { SubscriptionSuccess } from './components/subscription/SubscriptionSuccess';
import { LandingPage } from './components/landing/LandingPage';
import { TermsOfService } from './components/legal/TermsOfService';
import { PrivacyPolicy } from './components/legal/PrivacyPolicy';
import { Documentation } from './components/resources/Documentation';
import { Tutorials } from './components/resources/Tutorials';
import { Blog } from './components/resources/Blog';
import { BlogPost } from './components/resources/BlogPost';
import { FAQ } from './components/resources/FAQ';
import { NotFound } from './components/NotFound';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BackgroundGenerationProvider } from './contexts/BackgroundGenerationContext';
import { Toaster } from 'react-hot-toast';
import { IconProvider } from './components/IconProvider';
import { Footer } from './components/Footer';
import { SupportPage } from './components/support/SupportPage';
import { GenerationStatusWidget } from './components/GenerationStatusWidget';
import { BottomTray } from './components/ui/BottomTray';
import {
  AdminLayout,
  AdminRoute,
  AdminDashboard,
  UserManagement,
  SubscriptionManagement,
  SystemMaintenance,
  SettingsManagement,
  BlogManagement,
  SAMBatchImport
} from './components/admin';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

const SIDEBAR_COLLAPSE_KEY = 'bw_sidebar_collapsed';

function AppLayout({ children }: { children: React.ReactNode }) {
  const [isMenuCollapsed, setIsMenuCollapsed] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const handleCollapse = React.useCallback((collapsed: boolean) => {
    setIsMenuCollapsed(collapsed);
    try {
      localStorage.setItem(SIDEBAR_COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* storage unavailable — preference simply won't persist */
    }
  }, []);

  return (
    <div className="min-h-screen flex bg-bw-app">
      <Navbar isCollapsed={isMenuCollapsed} onCollapse={handleCollapse} />
      <div className={`flex-1 min-w-0 transition-all duration-200 ${isMenuCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
        <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-0">
          {children}
        </div>
      </div>
      {/* Mobile primary nav */}
      <BottomTray />
      {/* Background generation status widget */}
      <GenerationStatusWidget />
    </div>
  );
}

// Wrapper component to conditionally apply AppLayout
function ConditionalLayout({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  
  if (user) {
    return <AppLayout>{children}</AppLayout>;
  }
  
  return <>{children}</>;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <PublicRoute>
        <LandingPage />
      </PublicRoute>
    ),
  },
  {
    path: '/terms',
    element: <TermsOfService />,
  },
  {
    path: '/privacy',
    element: <PrivacyPolicy />,
  },
  {
    path: '/support',
    element: (
      <ConditionalLayout>
        <SupportPage />
      </ConditionalLayout>
    ),
  },
  {
    path: '/docs',
    element: (
      <ConditionalLayout>
        <Documentation />
      </ConditionalLayout>
    ),
  },
  {
    path: '/tutorials',
    element: (
      <ConditionalLayout>
        <Tutorials />
      </ConditionalLayout>
    ),
  },
  {
    path: '/blog',
    element: (
      <ConditionalLayout>
        <Blog />
      </ConditionalLayout>
    ),
  },
  {
    path: '/blog/:postId',
    element: (
      <ConditionalLayout>
        <BlogPost />
      </ConditionalLayout>
    ),
  },
  {
    path: '/faq',
    element: (
      <ConditionalLayout>
        <FAQ />
      </ConditionalLayout>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <PrivateRoute>
        <AppLayout>
          <ModernDashboard />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/profile',
    element: (
      <PrivateRoute>
        <AppLayout>
          <ProfilePage />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/workspace',
    element: (
      <PrivateRoute>
        <AppLayout>
          <WorkspaceLanding />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/workspace/:id',
    element: (
      <PrivateRoute>
        <AppLayout>
          <ProposalWorkspace />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/workspace/costing',
    element: (
      <PrivateRoute>
        <AppLayout>
          <CostingStudio />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/workspace/costing/wizard',
    element: (
      <PrivateRoute>
        <AppLayout>
          <PricingWizard />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/workspace/costing/clin-builder',
    element: (
      <PrivateRoute>
        <AppLayout>
          <CLINBuilder />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/workspace/costing/clin-builder/:id',
    element: (
      <PrivateRoute>
        <AppLayout>
          <CLINBuilder />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/workspace/costing/price-to-win',
    element: (
      <PrivateRoute>
        <AppLayout>
          <PriceToWinAnalysis />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/workspace/costing/labor',
    element: (
      <PrivateRoute>
        <AppLayout>
          <LaborCategoriesPage />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/workspace/costing/indirect-rates',
    element: (
      <PrivateRoute>
        <AppLayout>
          <IndirectRatesPage />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/workspace/costing/odc',
    element: (
      <PrivateRoute>
        <AppLayout>
          <ODCCategoriesPage />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/workspace/costing/wrap-rate',
    element: (
      <PrivateRoute>
        <AppLayout>
          <WrapRateCalculator />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/workspace/:id/pricing',
    element: (
      <PrivateRoute>
        <AppLayout>
          <ProposalPricingView />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/workspace/:id/price-to-win',
    element: (
      <PrivateRoute>
        <AppLayout>
          <PriceToWinAnalysis />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/subscription/plans',
    element: (
      <PrivateRoute>
        <AppLayout>
          <SubscriptionPage />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/subscription/success',
    element: (
      <PrivateRoute>
        <AppLayout>
          <SubscriptionSuccess />
        </AppLayout>
      </PrivateRoute>
    )
  },
  {
    path: '/admin',
    element: (
      <AdminRoute>
        <AppLayout>
          <AdminLayout />
        </AppLayout>
      </AdminRoute>
    ),
    children: [
      {
        index: true,
        element: <AdminDashboard />
      },
      {
        path: 'users',
        element: <UserManagement />
      },
      {
        path: 'subscriptions',
        element: <SubscriptionManagement />
      },
      {
        path: 'sam-import',
        element: <SAMBatchImport />
      },
      {
        path: 'system',
        element: <SystemMaintenance />
      },
      {
        path: 'settings',
        element: <SettingsManagement />
      },
      {
        path: 'blog',
        element: <BlogManagement />
      }
    ]
  },
  {
    path: '*',
    element: <NotFound />
  }
]);

function App() {
  return (
    <AuthProvider>
      <BackgroundGenerationProvider>
        <IconProvider>
          <RouterProvider router={router} />
          <Toaster position="top-right" />
        </IconProvider>
      </BackgroundGenerationProvider>
    </AuthProvider>
  );
}

export default App;